import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Package,
  ShoppingCart,
  FileText,
  CreditCard,
  X,
  Plus,
  Minus,
  CheckCircle,
  Boxes
} from 'lucide-react';

const EmptyStateMessage: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="text-center py-8">
    <Boxes className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500">{description}</p>
  </div>
);

interface QuickActionModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

interface NFEProduct {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

const QuickActionModal: React.FC<QuickActionModalProps> = ({ title, onClose, children }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const NFEForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [customer, setCustomer] = useState({
    name: '',
    document: '',
    email: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const [products, setProducts] = useState<NFEProduct[]>([
    {
      id: crypto.randomUUID(),
      name: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }
  ]);

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const addProduct = () => {
    setProducts([
      ...products,
      {
        id: crypto.randomUUID(),
        name: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }
    ]);
  };

  const removeProduct = (id: string) => {
    if (products.length > 1) {
      setProducts(products.filter(p => p.id !== id));
    }
  };

  const updateProduct = (id: string, field: keyof NFEProduct, value: string | number) => {
    setProducts(products.map(p => {
      if (p.id === id) {
        const updatedProduct = { ...p, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedProduct.total = updatedProduct.quantity * updatedProduct.unitPrice;
        }
        return updatedProduct;
      }
      return p;
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!customer.name) newErrors.name = 'Nome do cliente é obrigatório';
    if (!customer.document) newErrors.document = 'CPF/CNPJ é obrigatório';
    if (!customer.address.street) newErrors.street = 'Endereço é obrigatório';
    if (!customer.address.city) newErrors.city = 'Cidade é obrigatória';
    if (!customer.address.state) newErrors.state = 'Estado é obrigatório';

    const invalidProducts = products.some(p => !p.name || p.quantity <= 0 || p.unitPrice <= 0);
    if (invalidProducts) {
      newErrors.products = 'Todos os produtos precisam ter nome, quantidade e valor válidos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Simulate API call
    setTimeout(() => {
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    }, 1000);
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">NF-e emitida com sucesso!</h3>
        <p className="mt-2 text-gray-600">
          A nota fiscal foi gerada e será enviada por e-mail.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Cliente */}
      <div className="border-b border-gray-200 pb-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Dados do Cliente</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome/Razão Social*</label>
            <input
              type="text"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.name ? 'border-red-300' : ''
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">CPF/CNPJ*</label>
            <input
              type="text"
              value={customer.document}
              onChange={(e) => setCustomer({ ...customer, document: e.target.value })}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.document ? 'border-red-300' : ''
              }`}
            />
            {errors.document && <p className="mt-1 text-sm text-red-600">{errors.document}</p>}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">E-mail</label>
            <input
              type="email"
              value={customer.email}
              onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Endereço*</label>
            <input
              type="text"
              value={customer.address.street}
              onChange={(e) => setCustomer({
                ...customer,
                address: { ...customer.address, street: e.target.value }
              })}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.street ? 'border-red-300' : ''
              }`}
            />
            {errors.street && <p className="mt-1 text-sm text-red-600">{errors.street}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Número*</label>
            <input
              type="text"
              value={customer.address.number}
              onChange={(e) => setCustomer({
                ...customer,
                address: { ...customer.address, number: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cidade*</label>
            <input
              type="text"
              value={customer.address.city}
              onChange={(e) => setCustomer({
                ...customer,
                address: { ...customer.address, city: e.target.value }
              })}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.city ? 'border-red-300' : ''
              }`}
            />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Estado*</label>
            <input
              type="text"
              value={customer.address.state}
              onChange={(e) => setCustomer({
                ...customer,
                address: { ...customer.address, state: e.target.value }
              })}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                errors.state ? 'border-red-300' : ''
              }`}
            />
            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">CEP</label>
            <input
              type="text"
              value={customer.address.zipCode}
              onChange={(e) => setCustomer({
                ...customer,
                address: { ...customer.address, zipCode: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Produtos */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-medium text-gray-900">Produtos</h4>
          <button
            type="button"
            onClick={addProduct}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar Produto
          </button>
        </div>

        {errors.products && (
          <p className="mb-4 text-sm text-red-600">{errors.products}</p>
        )}

        <div className="space-y-4">
          {products.map((product, index) => (
            <div key={product.id} className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={product.name}
                  onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                  placeholder="Nome do Produto"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="w-24">
                <input
                  type="number"
                  value={product.quantity}
                  onChange={(e) => updateProduct(product.id, 'quantity', parseInt(e.target.value))}
                  min="1"
                  placeholder="Qtd"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="w-32">
                <input
                  type="number"
                  value={product.unitPrice}
                  onChange={(e) => updateProduct(product.id, 'unitPrice', parseFloat(e.target.value))}
                  step="0.01"
                  min="0"
                  placeholder="Valor Unit."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="w-32">
                <input
                  type="text"
                  value={`R$ ${product.total.toFixed(2)}`}
                  readOnly
                  className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>
              {products.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProduct(product.id)}
                  className="p-2 text-gray-400 hover:text-gray-500"
                >
                  <Minus className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <div className="w-48">
            <div className="text-sm font-medium text-gray-500">Total</div>
            <div className="text-lg font-bold text-gray-900">
              R$ {products.reduce((sum, p) => sum + p.total, 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Emitir NF-e
        </button>
      </div>
    </form>
  );
};

const PaymentForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <form className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Cliente
        </label>
        <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          <option>Selecione um cliente</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Valor
        </label>
        <input
          type="number"
          step="0.01"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="0,00"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Data do Pagamento
        </label>
        <input
          type="date"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div className="mt-5 sm:mt-6">
        <button
          type="submit"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
        >
          Registrar Pagamento
        </button>
      </div>
    </form>
  );
};

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const [currentModal, setCurrentModal] = useState<string | null>(null);

  const handleNewCustomer = () => {
    navigate('/app/customers', { state: { openNewCustomer: true } });
  };

  const handleNewProduct = () => {
    navigate('/app/products', { state: { openNewProduct: true } });
  };

  const handleNewSale = () => {
    navigate('/app/sales', { state: { openNewSale: true } });
  };

  const handleNewInvoice = () => {
    setCurrentModal('invoice');
  };

  const handleNewPayment = () => {
    setCurrentModal('payment');
  };

  const renderModal = () => {
    switch (currentModal) {
      case 'invoice':
        return (
          <QuickActionModal
            title="Emitir Nota Fiscal"
            onClose={() => setCurrentModal(null)}
          >
            <NFEForm onClose={() => setCurrentModal(null)} />
          </QuickActionModal>
        );

      case 'payment':
        return (
          <QuickActionModal
            title="Registrar Pagamento"
            onClose={() => setCurrentModal(null)}
          >
            <PaymentForm onClose={() => setCurrentModal(null)} />
          </QuickActionModal>
        );

      default:
        return null;
    }
  };

  const actions = [
    {
      icon: <UserPlus className="h-6 w-6" />,
      label: 'Novo Cliente',
      onClick: handleNewCustomer,
      color: 'text-blue-600'
    },
    {
      icon: <Package className="h-6 w-6" />,
      label: 'Novo Produto',
      onClick: handleNewProduct,
      color: 'text-green-600'
    },
    {
      icon: <ShoppingCart className="h-6 w-6" />,
      label: 'Nova Venda',
      onClick: handleNewSale,
      color: 'text-purple-600'
    },
    {
      icon: <FileText className="h-6 w-6" />,
      label: 'Emitir NF-e',
      onClick: handleNewInvoice,
      color: 'text-orange-600'
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      label: 'Registrar Pagamento',
      onClick: handleNewPayment,
      color: 'text-red-600'
    }
  ];

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className={`${action.color} mb-2`}>
              {action.icon}
            </div>
            <span className="text-sm font-medium text-gray-700 text-center">
              {action.label}
            </span>
          </button>
        ))}
      </div>

      {renderModal()}
    </>
  );
};

export default QuickActions;